import { el, svg } from "../core/dom";
import { part } from "../appearance/parts";
import type { Style } from "../appearance/types";
import { styles as classicStyles, type NoticeStyles } from "../styles/modal";
import type { Settings } from "../api/types";

export const LANGUAGE_NATIVE_LABELS: Record<string, string> = {
  English: "English",
  Assamese: "অসমীয়া (Assamese)",
  Bengali: "বাংলা (Bengali)",
  Bodo: "बड़ो (Bodo)",
  Dogri: "डोगरी (Dogri)",
  "Goan Konkani": "कोंकणी (Goan Konkani)",
  Gujarati: "ગુજરાતી (Gujarati)",
  Hindi: "हिन्दी (Hindi)",
  Kannada: "ಕನ್ನಡ (Kannada)",
  Kashmiri: "كٲشُر (Kashmiri)",
  Maithili: "मैथिली (Maithili)",
  Malayalam: "മലയാളം (Malayalam)",
  Manipuri: "ꯃꯩꯇꯩꯂꯣꯟ (Manipuri)",
  Marathi: "मराठी (Marathi)",
  Nepali: "नेपाली (Nepali)",
  Odia: "ଓଡ଼ିଆ (Odia)",
  Punjabi: "ਪੰਜਾਬੀ (Punjabi)",
  Sanskrit: "संस्कृतम् (Sanskrit)",
  Santali: "ᱥᱟᱱᱛᱟᱲᱤ (Santali)",
  Sindhi: "سنڌي (Sindhi)",
  Tamil: "தமிழ் (Tamil)",
  Telugu: "తెలుగు (Telugu)",
  Urdu: "اردو (Urdu)",
};

export const toNativeLabel = (name: string): string => LANGUAGE_NATIVE_LABELS[name] ?? name;

export type LanguageSelectorRefs = {
  container: HTMLElement;
  button: HTMLElement;
  dropdown: HTMLElement;
  buttonText: HTMLElement;
  close: () => void;
};

/** How a themed notice paints the chip and its menu; classic when absent. */
export type LanguageSelectorLook = {
  styles: NoticeStyles;
  responsive?: Record<string, Style>;
  chip: Style;
  menu: Style;
  selected: Style;
  /** The notice's root attributes, so its stylesheets and custom CSS reach the menu. */
  rootAttributes?: Record<string, string>;
};

const legacyLook = (settings: Partial<Settings> | undefined): LanguageSelectorLook => {
  const language = settings?.button?.language;
  const chip: Style = language
    ? { backgroundColor: language.backgroundColor, color: language.textColor }
    : {};
  const menu: Style = {};
  const selected: Style = {
    ...(language?.selectedBackgroundColor
      ? { backgroundColor: language.selectedBackgroundColor }
      : {}),
    ...(language?.selectedTextColor ? { color: language.selectedTextColor } : {}),
  };
  return { styles: classicStyles, chip, menu, selected };
};

export function renderLanguageSelector(options: {
  languages: string[];
  selectedLanguage: string;
  settings?: Partial<Settings>;
  /** Wins over `settings`: the console appearance resolved for the notice. */
  look?: LanguageSelectorLook;
  onSelect: (lang: string) => void;
  /**
   * Where the dropdown panel itself is appended. We append it as a sibling of
   * the modal (rather than inside `container`) so it can escape the modal's
   * `transform: translate(...)` stacking context. Pass the shadow root when
   * the modal is rendered inside a shadow boundary; otherwise pass
   * `document.body`.
   */
  dropdownContainer: HTMLElement | ShadowRoot;
}): LanguageSelectorRefs {
  const { languages, selectedLanguage, settings, onSelect, dropdownContainer } = options;
  const look = options.look ?? legacyLook(settings);
  const { styles } = look;

  let isOpen = false;

  const buttonText = el("span");
  buttonText.textContent = toNativeLabel(selectedLanguage);

  const chevronSvg = svg(
    "svg",
    {
      width: "8",
      height: "5",
      viewBox: "0 0 8 5",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
    },
    svg("path", {
      d: "M1 1L4 4L7 1",
      stroke: (look.chip.color as string | undefined) || "#344054",
      "stroke-width": "1.5",
      "stroke-linecap": "round",
      "stroke-linejoin": "round",
    })
  );

  const buttonStyle: Style = {
    ...styles.topRight,
    ...look.responsive?.topRight,
    ...look.chip,
  };

  const button = el("div", {
    ...part("language"),
    style: buttonStyle,
    role: "button",
    tabIndex: "0",
    "aria-haspopup": "listbox",
    "aria-expanded": "false",
    "aria-label": `Language selector, current: ${toNativeLabel(selectedLanguage)}`,
    onClick: () => toggleDropdown(),
    onKeydown: (e: KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        toggleDropdown();
      } else if (e.key === "Escape") {
        closeDropdown();
      }
    },
  }, buttonText, chevronSvg);

  // Dropdown — uses position:fixed to escape modal's transform stacking context
  const dropdown = el("div", {
    ...look.rootAttributes,
    style: {
      ...styles.languageDropdown,
      ...look.menu,
      position: "fixed",
      top: "auto",
      right: "auto",
      marginTop: 0,
      zIndex: 2147483647, // max int — always on top
      minWidth: "140px",
      width: "auto",
      display: "none",
    },
    role: "listbox",
    "aria-label": "Select language",
  });

  for (const lang of languages) {
    const isSelected = lang === selectedLanguage;
    const restingStyle: Style = {
      ...styles.languageItem,
      ...look.responsive?.languageItem,
      ...(isSelected ? { ...styles.selectedLanguageItem, ...look.selected } : look.menu),
    };
    const resting = (restingStyle.backgroundColor as string | undefined) ?? "";
    const hover = isSelected
      ? resting
      : ((look.selected.backgroundColor as string | undefined) ?? "#f3f4f6");

    const item = el("div", {
      style: restingStyle,
      role: "option",
      "aria-selected": String(isSelected),
      onClick: () => {
        onSelect(lang);
        closeDropdown();
      },
      onMouseenter: (e: Event) => {
        (e.target as HTMLElement).style.backgroundColor = hover;
      },
      onMouseleave: (e: Event) => {
        (e.target as HTMLElement).style.backgroundColor = resting;
      },
    });
    item.textContent = toNativeLabel(lang);
    dropdown.appendChild(item);
  }

  // Container only holds the button — dropdown is appended to its container
  // (shadow root in the modal flow, body for non-shadow callers) so it can
  // escape the modal's transform stacking context.
  const container = el("div", { style: styles.languageSelectorContainer }, button);

  dropdownContainer.appendChild(dropdown);

  function positionDropdown() {
    const rect = button.getBoundingClientRect();
    dropdown.style.top = `${rect.bottom + 4}px`;
    dropdown.style.right = `${window.innerWidth - rect.right}px`;
    dropdown.style.left = "auto";
  }

  // Click outside handler. When the modal is inside a shadow root the click
  // event's `target` is retargeted to the shadow host for listeners on the
  // document. `composedPath()` returns the real path through the shadow tree
  // so we can correctly detect whether the click originated inside the
  // selector or its dropdown.
  const clickOutsideHandler = (e: Event) => {
    const path = (e as Event & { composedPath?: () => EventTarget[] }).composedPath?.() ?? [];
    if (path.includes(container) || path.includes(dropdown)) return;

    // Fallback for environments without composedPath()
    if (path.length === 0) {
      const target = e.target as Node;
      if (container.contains(target) || dropdown.contains(target)) return;
    }
    closeDropdown();
  };

  // Reposition on scroll/resize while open
  const repositionHandler = () => {
    if (isOpen) positionDropdown();
  };

  function toggleDropdown() {
    isOpen ? closeDropdown() : openDropdown();
  }

  function openDropdown() {
    isOpen = true;
    positionDropdown();
    dropdown.style.display = "block";
    button.setAttribute("aria-expanded", "true");
    setTimeout(() => {
      document.addEventListener("click", clickOutsideHandler);
    }, 0);
    window.addEventListener("scroll", repositionHandler, true);
    window.addEventListener("resize", repositionHandler);
  }

  function closeDropdown() {
    isOpen = false;
    dropdown.style.display = "none";
    button.setAttribute("aria-expanded", "false");
    document.removeEventListener("click", clickOutsideHandler);
    window.removeEventListener("scroll", repositionHandler, true);
    window.removeEventListener("resize", repositionHandler);
  }

  return { container, button, dropdown, buttonText, close: closeDropdown };
}
